
import Vue from "vue";
import { Component, Watch } from "vue-property-decorator";

import PanelHeader from "@/components/Reusable/Util/PanelHeader.vue";
import RevisionItem from "@/components/Reusable/Revisions/RevisionItem.vue";

import { documentStore, uiStore } from "@/store/store-accessor";
import {
  DocumentRevision,
  DocumentRevisionsData,
  DocumentRevisionsDataFromServerV,
  RevisionUiItem
} from "@/types/revisions";
import { makeWordPressAjaxRequest } from "@/lib/wordpress/ajax";

import { exportTree, treesEqual } from "@/lib/tree/tree";
import { ExportedTree, ExportedTreeV } from "@/types/tree";

import { pipe } from "fp-ts/lib/function";
import { fold } from "fp-ts/lib/Either";
import { logErrorsToUiStore } from "@/lib/wordpress/data";
import { forceNextTick } from "@/util/force-next-tick";
import { getModuleNameFromMutation } from "@/store/util";
import RevisionsList from "@/components/Reusable/Revisions/RevisionsList.vue";
import fpJsonParseTypesafe from "@/util/fp-json-parse-typesafe";

@Component({
  components: { RevisionsList, RevisionItem, PanelHeader }
})
export default class Revisions extends Vue {
  loading = false;
  selectedRevisionIndex = 0;
  unsubscribeFromStore = () => {};

  revisionsNotSupportedMessage: string | null = null;

  get revisionUiItems() {
    return this.revisions.map(
      (revision, index): RevisionUiItem => ({
        revisionData: revision,
        isSelected: this.selectedRevisionIndex === index
      })
    );
  }

  async mounted() {
    this.loading = true;
    const remoteRevisions = await this.fetchRemoteRevisions();
    this.loading = false;
    if (!remoteRevisions) {
      return;
    }

    uiStore.setRevisionsData({
      revisions: remoteRevisions,
      currentUnsavedRevision: this.getCurrentUnsavedRevision(remoteRevisions)
    });

    this.setActiveRevision();

    this.unsubscribeFromStore = this.$store.subscribe(mutationPayload => {
      const moduleName = getModuleNameFromMutation(mutationPayload);

      if (
        moduleName === "document" &&
        mutationPayload.type !== "document/loadRevision"
      ) {
        uiStore.setRevisionsData({
          revisions: uiStore.revisionsData.revisions,
          currentUnsavedRevision: this.getCurrentUnsavedRevision(
            uiStore.revisionsData.revisions
          )
        });

        this.setActiveRevision();
      }
    });
  }

  destroyed() {
    this.unsubscribeFromStore();
  }

  get revisions(): DocumentRevision[] {
    return uiStore.revisionsData.currentUnsavedRevision
      ? [
          uiStore.revisionsData.currentUnsavedRevision,
          ...uiStore.revisionsData.revisions
        ]
      : uiStore.revisionsData.revisions;
  }

  get currentTree(): ExportedTree {
    return exportTree(documentStore.document.tree);
  }

  get unsavedChangesPresent() {
    return uiStore.unsavedChangesPresent;
  }

  @Watch("unsavedChangesPresent")
  async updateRevisionsAfterSaving() {
    if (!uiStore.unsavedChangesPresent) {
      const remoteRevisions = await this.fetchRemoteRevisions();
      if (remoteRevisions) {
        uiStore.setRevisionsData({
          revisions: remoteRevisions,
          // we just saved so there can't be any current revision
          currentUnsavedRevision: null
        });
      }

      this.setActiveRevision();
    }
  }

  setActiveRevision() {
    const activeRevisionIndex = this.getActiveRevisionIndex(
      uiStore.revisionsData.revisions
    );

    if (activeRevisionIndex !== -1) {
      this.selectedRevisionIndex = uiStore.revisionsData.currentUnsavedRevision
        ? // +1 for the current revision at the start of the array
          activeRevisionIndex + 1
        : activeRevisionIndex;

      this.scrollToActiveRevision("smooth");
    } else {
      this.selectedRevisionIndex = 0;
      this.scrollToActiveRevision("auto");
    }
  }

  getActiveRevisionIndex(revisions: DocumentRevision[]) {
    return revisions.findIndex(revision =>
      treesEqual(revision.tree, this.currentTree)
    );
  }

  getCurrentUnsavedRevision(
    revisions: DocumentRevision[]
  ): DocumentRevisionsData["currentUnsavedRevision"] {
    const activeRevision = this.getActiveRevisionIndex(revisions);

    if (activeRevision === -1) {
      return {
        id: -1,
        author: false,
        date: "Current",
        tree: this.currentTree
      };
    }

    // Prevent showing current revision after just saving
    const isCurrentRevisionTheSameAsTheMostRecentRevision =
      activeRevision === 0 &&
      uiStore.revisionsData.currentUnsavedRevision &&
      treesEqual(
        revisions[activeRevision].tree,
        uiStore.revisionsData.currentUnsavedRevision?.tree
      );

    if (isCurrentRevisionTheSameAsTheMostRecentRevision) {
      return null;
    }

    return uiStore.revisionsData.currentUnsavedRevision;
  }

  async fetchRemoteRevisions() {
    return pipe(
      await makeWordPressAjaxRequest({
        action: "breakdance_retrieve_revisions",
        decoder: DocumentRevisionsDataFromServerV,
        dataToPost: {
          id: documentStore.document.documentMeta.id
        }
      }),
      fold(
        errors => {
          logErrorsToUiStore(errors);

          return null;
        },
        data => {
          if ("unsupported" in data) {
            this.revisionsNotSupportedMessage = data.unsupported;

            return null;
          }

          return data.revisions
            .map(revision => {
              const maybeTree = fpJsonParseTypesafe(
                revision.tree,
                ExportedTreeV
              );

              const decodedTree = pipe(
                maybeTree,
                fold(
                  () => null,
                  tree => tree
                )
              );

              return {
                ...revision,
                tree: decodedTree
              };
            })
            .filter(
              (revision): revision is DocumentRevision => revision.tree !== null
            );
        }
      )
    );
  }

  onRevisionSelected(revisionUiItem: RevisionUiItem, index: number) {
    const revision = this.revisions.find(
      revision => revision.id === revisionUiItem.revisionData.id
    );

    if (revision) {
      this.selectedRevisionIndex = index;
      uiStore.activateElement(null);
      uiStore.setInterfaceState("loading");
      documentStore.loadRevision(revision);
      uiStore.setInterfaceState("normal");
    }
  }

  scrollToActiveRevision(behavior: ScrollIntoViewOptions["behavior"]) {
    forceNextTick(() =>
      document
        .querySelector(".revisions-panel .revisions-item--selected")
        ?.scrollIntoView({ behavior, block: "center" })
    );
  }
}
